import React, { useState, useEffect, useCallback } from 'react'
import { Building2, Plus, Pencil, Power, Trash2, Loader2, CornerDownRight } from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL
const EMPTY = { nom: '', siren: '', entite_mere_id: '', notes: '' }

export default function EntitesTab({ tenantId }) {
  const [entites, setEntites] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // null | 'new' | entite
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/entites?tenant_id=${tenantId}`)
      const d = await res.json()
      setEntites(d.entites || [])
    } catch { toast.error('Erreur chargement entites') } finally { setLoading(false) }
  }, [tenantId])

  useEffect(() => { load() }, [load])

  function openNew(mere) { setForm({ ...EMPTY, entite_mere_id: mere || '' }); setEditing('new') }
  function openEdit(e) { setForm({ nom: e.nom, siren: e.siren || '', entite_mere_id: e.entite_mere_id || '', notes: e.notes || '' }); setEditing(e) }

  async function save(ev) {
    ev.preventDefault()
    if (!form.nom.trim()) { toast.error('Nom requis'); return }
    setSaving(true)
    try {
      const isNew = editing === 'new'
      const body = { nom: form.nom.trim(), siren: form.siren || null, entite_mere_id: form.entite_mere_id || (isNew ? null : ''), notes: form.notes || null }
      const res = await fetch(isNew ? `${API}/api/entites` : `${API}/api/entites/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isNew ? { ...body, tenant_id: tenantId } : body),
      })
      const d = await res.json()
      if (!res.ok) { toast.error(d.detail || 'Erreur'); return }
      toast.success(isNew ? 'Entite creee' : 'Entite mise a jour'); setEditing(null); load()
    } catch { toast.error('Erreur reseau') } finally { setSaving(false) }
  }

  async function toggleActif(e) {
    const res = await fetch(`${API}/api/entites/${e.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actif: !e.actif }) })
    if (res.ok) { toast.success(e.actif ? 'Entite desactivee' : 'Entite reactivee'); load() } else toast.error('Erreur')
  }

  async function remove(e) {
    if (!window.confirm(`Supprimer definitivement « ${e.nom} » ? (refuse si des donnees y sont rattachees)`)) return
    const res = await fetch(`${API}/api/entites/${e.id}`, { method: 'DELETE' })
    const d = await res.json()
    if (res.ok) { toast.success('Entite supprimee'); load() } else toast.error(d.detail || 'Suppression refusee')
  }

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>

  return (
    <div className="space-y-4" data-testid="entites-tab-content">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Building2 size={16} className="text-indigo-600" /> Entites juridiques</h3>
          <p className="text-xs text-slate-500">Groupe et filiales. Une police ad valorem se rattache a une entite ; la police d'une mere couvre ses filles. Les agences sont des sites, pas des entites.</p>
        </div>
        <button onClick={() => openNew()} className="flex items-center gap-1 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-testid="btn-new-entite"><Plus size={14} /> Nouvelle entite</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm" data-testid="form-entite">
          <div className="md:col-span-1"><label className="text-xs text-slate-500">Nom *</label><input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" autoFocus data-testid="input-entite-nom" /></div>
          <div><label className="text-xs text-slate-500">SIREN</label><input value={form.siren} onChange={e => setForm({ ...form, siren: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" data-testid="input-entite-siren" /></div>
          <div>
            <label className="text-xs text-slate-500">Entite mere</label>
            <select value={form.entite_mere_id} onChange={e => setForm({ ...form, entite_mere_id: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg bg-white" data-testid="select-entite-mere">
              <option value="">— aucune (tete de groupe) —</option>
              {entites.filter(e => editing === 'new' || e.id !== editing.id).map(e => <option key={e.id} value={e.id}>{e.chemin}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-500">Notes</label><input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" /></div>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-200">Annuler</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white disabled:opacity-50" data-testid="submit-entite">{saving ? '…' : editing === 'new' ? 'Creer' : 'Enregistrer'}</button>
          </div>
        </form>
      )}

      {entites.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">Aucune entite. Les clients mono-societe n'en ont pas besoin.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left px-4 py-2">Entite</th><th className="text-left px-4 py-2">SIREN</th><th className="text-left px-4 py-2">Filles</th><th className="text-left px-4 py-2">Statut</th><th className="px-4 py-2" /></tr></thead>
            <tbody>
              {entites.map(e => (
                <tr key={e.id} className={`border-t border-slate-100 ${e.actif ? '' : 'opacity-50'}`} data-testid={`entite-row-${e.id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1" style={{ paddingLeft: `${e.niveau * 18}px` }}>
                      {e.niveau > 0 && <CornerDownRight size={12} className="text-slate-300" />}
                      <span className="font-medium text-slate-800">{e.nom}</span>
                      {e.niveau === 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">groupe</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{e.siren || '--'}</td>
                  <td className="px-4 py-2 text-slate-500">{e.nb_filles}</td>
                  <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${e.actif ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{e.actif ? 'Active' : 'Desactivee'}</span></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openNew(e.id)} title="Ajouter une filiale" className="p-1.5 text-slate-400 hover:text-indigo-600" data-testid={`btn-add-fille-${e.id}`}><Plus size={14} /></button>
                    <button onClick={() => openEdit(e)} title="Modifier" className="p-1.5 text-slate-400 hover:text-indigo-600" data-testid={`btn-edit-entite-${e.id}`}><Pencil size={14} /></button>
                    <button onClick={() => toggleActif(e)} title={e.actif ? 'Desactiver' : 'Reactiver'} className="p-1.5 text-slate-400 hover:text-amber-600" data-testid={`btn-toggle-entite-${e.id}`}><Power size={14} /></button>
                    <button onClick={() => remove(e)} title="Supprimer" className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`btn-delete-entite-${e.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
